import { fetchAdministrationOrganizationPeople, mutateAdministrationOrganizationPeople } from "../../api/administrationDashboard";
import { createKeyedResourceLoader } from "./keyedResourceLoader";
import { refreshOrganizationPeople } from "./refresh";
import { AdministrationOrganizationPeopleQuery, AdministrationSlice, OrganizationPeopleSlice } from "./types";

const loader = createKeyedResourceLoader();
export const administrationOrganizationPeopleCacheKey = (id: string, query: AdministrationOrganizationPeopleQuery) => `${id}:${query.memberLimit}:${query.memberOffset}:${query.invitationLimit}:${query.invitationOffset}`;
export const organizationPeopleInitialState = { organizationPeople: {}, organizationPeopleQueries: {}, organizationPeopleLoading: {}, organizationPeopleErrors: {}, organizationPeopleActionLoading: {} };
export const resetOrganizationPeopleRequests = () => loader.clear();

const optimisticallyApplyPeopleMutation = (
  people: OrganizationPeopleSlice["organizationPeople"],
  queries: OrganizationPeopleSlice["organizationPeopleQueries"],
  organizationId: string,
  mutation: Parameters<OrganizationPeopleSlice["runOrganizationPeopleMutation"]>[1],
) => Object.fromEntries(Object.entries(people).map(([key, page]) => {
  if (queries[key]?.id !== organizationId) return [key, page];
  if (mutation.type === "invite") {
    const alreadyVisible = page.invitations.some((invite) => invite.email.toLowerCase() === mutation.email.toLowerCase());
    const invitation = {
      id: `pending-${Date.now()}-${mutation.email}`,
      email: mutation.email,
      role: mutation.role,
      target: "organization" as const,
      locationId: null,
      locationName: null,
      invitedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60_000).toISOString(),
    };
    return [key, {
      ...page,
      invitations: page.invitationOffset === 0 && !alreadyVisible
        ? [invitation, ...page.invitations].slice(0, page.invitationLimit)
        : page.invitations,
      invitationTotal: alreadyVisible ? page.invitationTotal : page.invitationTotal + 1,
    }];
  }
  if (mutation.type === "revoke-invitation") {
    return [key, {
      ...page,
      invitations: page.invitations.filter((invite) => invite.id !== mutation.invitationId),
      invitationTotal: Math.max(0, page.invitationTotal - 1),
    }];
  }
  if (mutation.type === "resend-invitation") {
    const invitedAt = new Date().toISOString();
    return [key, {
      ...page,
      invitations: page.invitations.map((invite) => invite.id === mutation.invitationId
        ? { ...invite, invitedAt }
        : invite),
    }];
  }
  if (mutation.type === "change-role") {
    return [key, {
      ...page,
      members: page.members.map((member) => member.id === mutation.userId
        ? { ...member, role: mutation.role }
        : member),
    }];
  }
  return [key, {
    ...page,
    members: page.members.filter((member) => member.id !== mutation.userId),
    memberTotal: Math.max(0, page.memberTotal - 1),
  }];
}));

export const createOrganizationPeopleSlice: AdministrationSlice<OrganizationPeopleSlice> = (set, get) => ({
  ...organizationPeopleInitialState,
  loadOrganizationPeople: (id, query = {}, force = false) => {
    const resolved = { memberLimit: query.memberLimit ?? 25, memberOffset: query.memberOffset ?? 0, invitationLimit: query.invitationLimit ?? 25, invitationOffset: query.invitationOffset ?? 0 };
    const key = administrationOrganizationPeopleCacheKey(id, resolved);
    return loader.load({ key, force, cached: Boolean(get().organizationPeople[key]), request: () => fetchAdministrationOrganizationPeople(id, resolved),
      onStart: () => set((state) => ({ organizationPeopleQueries: { ...state.organizationPeopleQueries, [key]: { id, ...resolved } }, organizationPeopleLoading: { ...state.organizationPeopleLoading, [key]: true }, organizationPeopleErrors: { ...state.organizationPeopleErrors, [key]: "" } })),
      onSuccess: (people) => set((state) => ({ organizationPeople: { ...state.organizationPeople, [key]: people } })),
      onError: (error) => set((state) => ({ organizationPeopleErrors: { ...state.organizationPeopleErrors, [key]: error } })),
      onFinish: () => set((state) => ({ organizationPeopleLoading: { ...state.organizationPeopleLoading, [key]: false } })), errorMessage: "Unable to load organization people." });
  },
  runOrganizationPeopleMutation: async (id, mutation) => {
    if (get().organizationPeopleActionLoading[id]) return;
    const previousPeople = get().organizationPeople;
    set((state) => ({
      organizationPeople: optimisticallyApplyPeopleMutation(
        state.organizationPeople,
        state.organizationPeopleQueries,
        id,
        mutation,
      ),
      organizationPeopleActionLoading: { ...state.organizationPeopleActionLoading, [id]: mutation.type },
    }));
    try {
      await mutateAdministrationOrganizationPeople(id, mutation);
      void get().refreshOrganizationPeopleScope(id);
    } catch (error) {
      set({ organizationPeople: previousPeople });
      throw error;
    }
    finally { set((state) => ({ organizationPeopleActionLoading: { ...state.organizationPeopleActionLoading, [id]: null } })); }
  },
  refreshOrganizationPeopleScope: (id) => refreshOrganizationPeople(get(), id),
});
